import React, { useState } from 'react';
import { IThspianPlaylist } from '../../../types/global/helper';
import { Box, Typography } from '@mui/material';
import CustomLazyLoadImage from '../../Image/CustomLazyLoadImage';
import { useThemeContext } from '../../../hook/useThemeManager';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { theme } from '../../../constants/theme';
import { FullScreenMoreMenu } from '../FullScreenMoreMenu';
import { generateShareLink } from '../../../utils/AppUtils';
import { DialogueSheet } from '../../DialogueSheet/DialogueSheet';

export interface IPlaylistCard {
    size?: number;
    playlist: IThspianPlaylist;
    onDeletePlaylist?: (playlist: IThspianPlaylist) => void;
    onPreviewPlaylist?: (playlist: IThspianPlaylist) => void;
    onEditPlaylist?: (playlist: IThspianPlaylist) => void;
    showIconOnly?: boolean;
    iconSx?: object;
}

export const MENU_PLAYLIST = [
    {
        id: 'edit',
        title: 'Edit Playlist',
    },
    {
        id: 'delete',
        title: 'Delete Playlist',
    },
    {
        id: 'link',
        title: 'Copy Playlist Link',
    },
    {
        id: 'preview',
        title: 'Preview Playlist',
    },
];
export const MAX_VISIBLE_ICONS = 3; // Limit the number of visible icons to 3

export const PlaylistCardForGrid: React.FC<IPlaylistCard> = ({
    playlist,
    onDeletePlaylist,
    onEditPlaylist,
    onPreviewPlaylist,
    showIconOnly,
    iconSx,
}) => {
    const { device, themeStyle } = useThemeContext();

    const ICON_SIZE = '100%'; // Base icon size
    const [showDeleteDialogue, setShowDeleteDialogue] = useState(false);

    const handleMoreItemClick = (moreType: string) => {
        switch (moreType) {
            case 'edit':
                onEditPlaylist && onEditPlaylist(playlist);
                break;
            case 'delete':
                setShowDeleteDialogue(!showDeleteDialogue);
                break;
            case 'link':
                generateShareLink('playlist', playlist);
                break;
            case 'preview':
                onPreviewPlaylist && onPreviewPlaylist(playlist);
                break;
        }
    };

    if (showIconOnly) {
        return (
            <>
                <FullScreenMoreMenu
                    menu={MENU_PLAYLIST}
                    onClick={handleMoreItemClick}
                    title={playlist.name}
                    customIconSx={iconSx}
                />
                <DialogueSheet
                    onClose={() => setShowDeleteDialogue(false)}
                    open={showDeleteDialogue}
                    onAgreeClicked={() => {
                        onDeletePlaylist && onDeletePlaylist(playlist);
                        setShowDeleteDialogue(false);
                    }}
                    onDisagreeClick={() => setShowDeleteDialogue(false)}
                    positiveTextStyle={{
                        color: theme[themeStyle].secondary_red,
                    }}
                    positiveText="Yes"
                    negativeText="No"
                    header="Delete playlist?"
                    body="This action cannot be reversed. Will you like to continue?"
                />
            </>
        );
    }

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'flex',
                width: {
                    xs: '100%',
                    xsSm: '100%',
                    sm: '100%',
                    mdLg: '90%',
                    lg: '95%',
                    xl: '100%',
                },
                maxWidth: '100%',
                minWidth: 0,
                height: {
                    xs: '240px',
                    xsSm: '300px',
                    sm: '260px',
                    smMd: '250px',
                    md: '260px',
                    xl: '320px',
                },
                marginBottom: {
                    lg: '5px',
                },
                justifyContent: 'flex-start',
                alignItems: 'center',
                mt: 2,
                boxShadow: 3,
                borderRadius: '15px',
            }}>
            <FullScreenMoreMenu
                menu={MENU_PLAYLIST}
                onClick={handleMoreItemClick}
                title={playlist.name}
                customIconSx={{
                    top: 0,
                    right: 0,
                    position: 'absolute',
                    zIndex: 12,
                }}
                sx={{
                    backgroundColor: theme[themeStyle].true_black,
                    color: theme[themeStyle].true_white,
                    padding: '2px',
                    height: '30px',
                    width: '30px',
                    borderRadius: '4px',
                }}
            />
            <DialogueSheet
                onClose={() => setShowDeleteDialogue(false)}
                open={showDeleteDialogue}
                onAgreeClicked={() => {
                    onDeletePlaylist && onDeletePlaylist(playlist);
                    setShowDeleteDialogue(false);
                }}
                onDisagreeClick={() => setShowDeleteDialogue(false)}
                positiveTextStyle={{
                    color: theme[themeStyle].secondary_red,
                }}
                positiveText="Yes"
                negativeText="No"
                header="Delete playlist?"
                body="This action cannot be reversed. Will you like to continue?"
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    zIndex: 11,
                    p: 2,
                }}>
                <Typography
                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].true_white, fontSize: 12 }}
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                    }}>
                    {playlist.name}
                </Typography>
                <Typography
                    style={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, color: theme[themeStyle].true_white, fontSize: 8 }}
                    sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                    }}>
                    {playlist.description}
                </Typography>
            </Box>
            <Box
                position="absolute"
                bottom={0}
                left={0}
                width="100%"
                height="80%"
                sx={{
                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent)',
                    pointerEvents: 'none',
                    zIndex: 10,
                    overflow: 'hidden',
                    borderRadius: 3,
                }}
            />
            {playlist.projects
                .slice(0, MAX_VISIBLE_ICONS)
                .filter(p => p && p.project && !!p.feature_image)
                .map((project, index) => {
                    const size = ICON_SIZE; // Decrease size by 10px for each subsequent icon
                    const zIndex = MAX_VISIBLE_ICONS - index; // Control z-index to keep them stacked correctly

                    return (
                        <CustomLazyLoadImage
                            key={index}
                            src={project.feature_image}
                            onClick={() => onPreviewPlaylist && onPreviewPlaylist(playlist)}
                            showGradient
                            gradientStyle={{
                                borderRadius: 3,
                            }}
                            style={{
                                height: '100%',
                                width: size,
                                zIndex: zIndex,
                                position: 'absolute', // Use relative position for stacking
                                border: `0.1px solid white`,
                                borderRadius: '15px',
                                objectFit: 'cover',
                                cursor: 'pointer',
                                opacity: 1,
                                ...(index === 1 && { top: -9, width: device.isMobile ? '95%' : '90%', left: 10 }),
                                ...(index === 2 && { top: -15, width: device.isMobile ? '90%' : '85%', left: 15 }),
                            }}
                            alt={project.title}
                        />
                    );
                })}
        </Box>
    );
};
